// src/services/financeService.js
import { 
    collection, 
    query, 
    orderBy, 
    getDocs, 
    doc, 
    getDoc, 
    setDoc, 
    updateDoc, 
    deleteDoc, 
    runTransaction, 
    serverTimestamp,
    Timestamp 
} from 'firebase/firestore';
import { 
    getStorage, 
    ref, 
    uploadBytes, 
    getDownloadURL, 
    deleteObject 
} from 'firebase/storage';
import { db } from '../config/firebase';

const EXPENSES_COL = 'expenses';
const ACCOUNTING_COL = 'accounting'; // Para leer ingresos
const PARAMS_COL = 'parameters';
const COUNTER_COL = 'expenseCounter';

export const financeService = {
    /**
     * Obtiene todos los datos necesarios para el Dashboard Financiero en una sola llamada paralela.
     * Retorna: { expenses, accountingRecords, parameters }
     */
    getAllFinancialData: async () => {
        try {
            const [expSnap, accSnap, paramsSnap] = await Promise.all([
                getDocs(query(collection(db, EXPENSES_COL), orderBy('date', 'desc'))),
                getDocs(query(collection(db, ACCOUNTING_COL), orderBy('createdAt', 'desc'))),
                getDoc(doc(db, PARAMS_COL, 'system-settings'))
            ]);

            const expenses = expSnap.docs.map(d => ({ 
                id: d.id, 
                ...d.data(), 
                amount: parseFloat(d.data().amount) || 0 
            }));

            const accountingRecords = accSnap.docs.map(d => {
                const data = d.data();
                // Cálculo de lo pagado realmente
                let realPaid = 0;
                if (data.payments && Array.isArray(data.payments)) {
                    realPaid = data.payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
                } else {
                    realPaid = parseFloat(data.paidAmount) || 0;
                }
                return {
                    id: d.id,
                    ...data,
                    totalAmount: parseFloat(data.totalAmount) || 0,
                    paidAmount: realPaid
                };
            });

            const parameters = paramsSnap.exists() 
                ? { iva: 19, ppm: 1.5, ...paramsSnap.data() } 
                : { iva: 19, ppm: 1.5 };

            return { expenses, accountingRecords, parameters };

        } catch (error) {
            console.error("Error obteniendo datos financieros:", error);
            throw error;
        }
    },

    /**
     * Genera el siguiente ID de egreso usando una transacción para evitar duplicados.
     * Retorna strings tipo "EGR-0000123".
     */
    generateNextExpenseId: async () => {
        const counterRef = doc(db, COUNTER_COL, 'counter');
        try {
            const newId = await runTransaction(db, async (transaction) => {
                const docSnap = await transaction.get(counterRef);
                if (!docSnap.exists()) {
                    transaction.set(counterRef, { current: 1 });
                    return 1;
                }
                const newCount = docSnap.data().current + 1;
                transaction.update(counterRef, { current: newCount });
                return newCount;
            });
            return `EGR-${String(newId).padStart(7, '0')}`;
        } catch (error) {
            console.error("Error generando ID de egreso:", error);
            throw error;
        }
    },

    /**
     * Sube un archivo a Firebase Storage.
     */
    uploadFile: async (file, folderId) => {
        if (!file) return null;
        try {
            const storage = getStorage();
            const storagePath = `finance-docs/${folderId}/${file.name}`;
            const fileRef = ref(storage, storagePath);
            await uploadBytes(fileRef, file);
            const downloadUrl = await getDownloadURL(fileRef);
            return { url: downloadUrl, path: storagePath };
        } catch (error) {
            console.error("Error subiendo archivo:", error);
            throw error;
        }
    },

    /**
     * Guarda un gasto (Crear o Editar). Maneja la subida de archivos internamente si se provee uno.
     */
    saveExpense: async (expenseData, file = null, isEditMode = false) => {
        try {
            let finalData = { ...expenseData };
            
            // 1. Si hay archivo nuevo, subirlo
            if (file) {
                const uploadResult = await financeService.uploadFile(file, finalData.id); // Usamos el ID (folio) como carpeta
                finalData.fileUrl = uploadResult.url;
                finalData.storagePath = uploadResult.path;
            }

            // 2. Convertir fecha string a Timestamp para Firestore
            if (typeof finalData.date === 'string') {
                finalData.date = Timestamp.fromDate(new Date(finalData.date + "T12:00:00"));
            }

            finalData.updatedAt = serverTimestamp();
            if (!isEditMode) {
                finalData.createdAt = serverTimestamp();
            }

            // 3. Guardar en Firestore
            const docRef = doc(db, EXPENSES_COL, finalData.id);
            if (isEditMode) {
                await updateDoc(docRef, finalData);
            } else {
                await setDoc(docRef, finalData);
            }

            return finalData.id;
        } catch (error) {
            console.error("Error guardando gasto:", error);
            throw error;
        }
    },

    /**
     * Elimina un gasto y su archivo adjunto si existe.
     */
    deleteExpense: async (expense) => {
        try {
            // 1. Borrar archivo de Storage
            if (expense.storagePath) {
                const storage = getStorage();
                const fileRef = ref(storage, expense.storagePath);
                await deleteObject(fileRef).catch(err => console.warn("Archivo no encontrado, continuando...", err));
            }
            // 2. Borrar documento
            await deleteDoc(doc(db, EXPENSES_COL, expense.id));
        } catch (error) {
            console.error("Error eliminando gasto:", error);
            throw error;
        }
    }
};